import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const WHATSAPP_NUMBER = '919840264444';

const getPageMessage = (pathname) => {
  switch (pathname) {
    case '/gynaecology': return 'Hi! I would like to book an appointment for Gynaecology & Maternity at Sakthi Hospital.';
    case '/urology': return 'Hi! I would like to consult Dr. Venkat for Urology at Sakthi Hospital.';
    case '/cardiology': return 'Hi! I would like to book a Cardiology / Heart Check-Up at Sakthi Hospital.';
    case '/orthopaedics': return 'Hi! I would like to consult an Orthopaedic specialist at Sakthi Hospital.';
    default: return 'Hello! I would like to book an appointment at Sakthi Hospital.';
  }
};

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const location = useLocation();
  const pageMessage = getPageMessage(location.pathname);

  // Hide tooltip after 5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowTooltip(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(pageMessage)}`;
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'whatsapp_click', wa_source: location.pathname, wa_message: pageMessage });
    }
    window.open(url, '_blank');
  };

  const quickMessages = [
    { text: 'Book an Appointment', message: `Hi! I want to book an appointment for ${location.pathname === '/' ? 'a consultation' : location.pathname.replace('/', '').charAt(0).toUpperCase() + location.pathname.replace('/', '').slice(1)} at Sakthi Hospital.` },
    { text: 'Emergency Query', message: 'Hello! I have an emergency. Is a doctor available now at Sakthi Hospital?' },
    { text: 'Get Directions', message: 'Hello! Can you share the exact location of Sakthi Hospital, Triplicane?' },
    { text: 'Insurance Query', message: 'Hello! I want to check if my insurance is accepted at Sakthi Hospital for cashless treatment.' },
  ];

  return (
    <>
      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-24 right-6 z-50" data-testid="whatsapp-widget">
        {/* Tooltip */}
        {showTooltip && !isOpen && (
          <div className="absolute bottom-full right-0 mb-3 animate-fade-in">
            <div className="bg-white rounded-lg shadow-lg px-4 py-2 text-sm font-medium text-gray-700 whitespace-nowrap">
              Chat with us on WhatsApp!
              <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-2 h-2 bg-white"></div>
            </div>
          </div>
        )}

        {/* Quick Actions Panel */}
        {isOpen && (
          <div className="absolute bottom-20 right-0 w-72 bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in border border-gray-100">
            {/* Header */}
            <div className="bg-[#25D366] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#25D366]" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">Sakthi Hospital</div>
                    <div className="text-white/80 text-xs">Typically replies instantly</div>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Chat Preview */}
            <div className="p-4 bg-[#ECE5DD]">
              <div className="bg-white rounded-lg p-3 shadow-sm max-w-[80%]">
                <p className="text-sm text-gray-700">
                  👋 Hi! How can we help you today?
                </p>
                <span className="text-xs text-gray-400 mt-1 block">Just now</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-4 space-y-2">
              <p className="text-xs text-gray-500 font-medium mb-3">Quick Actions</p>
              {quickMessages.map((item) => (
                <button
                  key={item.text}
                  onClick={() => {
                    if (window.dataLayer) { window.dataLayer.push({ event: 'whatsapp_click', wa_source: location.pathname, wa_action: item.text }); }
                    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(item.message)}`;
                    window.open(url, '_blank');
                  }}
                  className="w-full text-left px-4 py-2.5 bg-gray-50 hover:bg-[#25D366]/10 rounded-lg text-sm font-medium text-gray-700 hover:text-[#25D366] transition-colors"
                  data-testid={`whatsapp-quick-${item.text}`}
                >
                  {item.text}
                </button>
              ))}
            </div>

            {/* Start Chat Button */}
            <div className="p-4 pt-0">
              <button
                onClick={handleWhatsAppClick}
                className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="whatsapp-start-chat"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Start Chat
              </button>
            </div>
          </div>
        )}

        {/* Main WhatsApp Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
            isOpen ? 'bg-gray-600 rotate-90' : 'bg-[#25D366]'
          }`}
          data-testid="whatsapp-toggle"
        >
          {isOpen ? (
            <X size={28} className="text-white" />
          ) : (
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          )}
        </button>

        {/* Pulse Animation */}
        {!isOpen && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30 pointer-events-none"></span>
        )}
      </div>
    </>
  );
};

export default WhatsAppButton;
